import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

export const auditEventTypeEnum = z.enum([
  'status_change',
  'assignment',
  'completion',
  'authority_override',
  'field_update',
  'creation',
  'deletion',
]);

export const auditEntityTypeEnum = z.enum(['job', 'work_order', 'user']);

export const auditTrailItemSchema = z.object({
  id: z.number(),
  trace_id: z.string(),
  entity_type: auditEntityTypeEnum,
  entity_id: z.number(),
  event_type: auditEventTypeEnum,
  field_name: z.string().nullable(),
  previous_value: z.unknown().nullable(),
  new_value: z.unknown().nullable(),
  reason: z.string().nullable(),
  user_id: z.number(),
  username: z.string(),
  metadata: z.unknown().nullable(),
  created_at: z.string(),
});

export const auditTrailResponseSchema = z.object({
  data: z.array(auditTrailItemSchema),
  total: z.number(),
  limit: z.number(),
  skip: z.number(),
});

export const auditTrailQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  skip: z.coerce.number().int().min(0).default(0),
});

export const auditTrailSearchQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  skip: z.coerce.number().int().min(0).default(0),
  entity_type: auditEntityTypeEnum.optional(),
  entity_id: z.coerce.number().int().optional(),
  event_type: auditEventTypeEnum.optional(),
  user_id: z.coerce.number().int().optional(),
  from_date: z.string().optional(),
  to_date: z.string().optional(),
});

export type AuditTrailItem = z.infer<typeof auditTrailItemSchema>;
export type AuditTrailResponse = z.infer<typeof auditTrailResponseSchema>;
export type AuditTrailQuery = z.infer<typeof auditTrailQuerySchema>;
export type AuditTrailSearchQuery = z.infer<typeof auditTrailSearchQuerySchema>;
export type AuditEventType = z.infer<typeof auditEventTypeEnum>;
export type AuditEntityType = z.infer<typeof auditEntityTypeEnum>;

export const auditRetentionBodySchema = z.object({
  retentionDays: z.number().int().min(1).optional(),
});

export const auditRetentionResponseSchema = z.object({
  deletedCount: z.number(),
});

export type AuditRetentionBody = z.infer<typeof auditRetentionBodySchema>;
export type AuditRetentionResponse = z.infer<typeof auditRetentionResponseSchema>;

export const auditTrailContract = c.router({
  getAuditTrailForEntity: {
    method: 'GET',
    path: '/entity/:entityType/:entityId',
    pathParams: z.object({
      entityType: auditEntityTypeEnum,
      entityId: z.coerce.number(),
    }),
    query: auditTrailQuerySchema,
    responses: {
      200: auditTrailResponseSchema,
      500: z.object({
        error: z.object({
          message: z.string(),
          code: z.string().optional(),
        }),
      }),
    },
    summary: 'Get audit trail entries for a specific entity',
  },
  searchAuditTrail: {
    method: 'GET',
    path: '/search',
    query: auditTrailSearchQuerySchema,
    responses: {
      200: auditTrailResponseSchema,
      500: z.object({
        error: z.object({
          message: z.string(),
          code: z.string().optional(),
        }),
      }),
    },
    summary: 'Search audit trail with filters',
  },
  purgeExpiredEntries: {
    method: 'POST',
    path: '/retention/purge',
    body: auditRetentionBodySchema,
    responses: {
      200: auditRetentionResponseSchema,
      500: z.object({
        error: z.object({
          message: z.string(),
          code: z.string().optional(),
        }),
      }),
    },
    summary: 'Purge expired audit trail entries based on retention policy',
  },
});
